import { CalendarEventMdbFilter } from "../types";
import { compileFreeformFilter, type FreeformFilter, isFreeformFilterValueComplete } from "./freeform";

export type FreeformFilterLogic = "and" | "or";

export interface FreeformFilterBlock {
  id: string;
  logic: FreeformFilterLogic;
  filters: FreeformFilter[];
}

export interface CalendarFilters {
  templateIds?: string[];
  locationIds?: string[];
  userIds?: string[];
  enduserIds?: string[];
  hideCancelled?: boolean;
  hideNoShow?: boolean;
  hideRescheduled?: boolean;
  hideEventsICreatedButAmNotAttending?: boolean;
  customBlocks?: FreeformFilterBlock[];
}

export const DEFAULT_CALENDAR_FILTERS: CalendarFilters = {};

/**
 * Sentinel user id used to force an empty result set when a user selection exists
 * but its intersection with the active view filters is empty. It never matches a
 * real user and must be excluded anywhere selected users are surfaced to the UI.
 */
export const FAKE_CALENDAR_USER_ID = "__fake_id__";

const compileBlock = (block: FreeformFilterBlock): Record<string, unknown> | undefined => {
  const completeFilters = block.filters.filter(isFreeformFilterValueComplete);
  if (!completeFilters.length) return undefined;

  if (block.logic === "and") {
    return completeFilters.reduce<Record<string, unknown>>((acc, filter) => {
      Object.assign(acc, compileFreeformFilter(filter));
      return acc;
    }, {});
  }

  return { $or: completeFilters.map(compileFreeformFilter) };
};

export const buildCalendarMdbFilter = (
  filters: CalendarFilters | undefined,
  currentUserId: string | undefined
): CalendarEventMdbFilter => {
  if (!filters) return {};

  const andClauses: Record<string, unknown>[] = [];
  const top: Record<string, unknown> = {};

  if (filters.templateIds?.length) {
    top.templateId = { $in: filters.templateIds };
  }

  if (filters.locationIds?.length) {
    top.locationIds = { $in: filters.locationIds };
  }

  const userAttendeesClause = filters.userIds?.length
    ? { attendees: { $elemMatch: { id: { $in: filters.userIds }, type: "user" } } }
    : undefined;
  const enduserAttendeesClause = filters.enduserIds?.length
    ? { attendees: { $elemMatch: { id: { $in: filters.enduserIds }, type: "enduser" } } }
    : undefined;

  if (userAttendeesClause && enduserAttendeesClause) {
    andClauses.push(userAttendeesClause, enduserAttendeesClause);
  } else if (userAttendeesClause) {
    Object.assign(top, userAttendeesClause);
  } else if (enduserAttendeesClause) {
    Object.assign(top, enduserAttendeesClause);
  }

  if (filters.hideCancelled) {
    top.cancelledAt = { $in: [null, ""] };
  }
  if (filters.hideNoShow) {
    top.noShowedAt = { $in: [null, ""] };
  }
  if (filters.hideRescheduled) {
    top.rescheduledAt = { $in: [null, ""] };
  }

  if (filters.hideEventsICreatedButAmNotAttending && currentUserId) {
    andClauses.push({
      $or: [{ creator: { $ne: currentUserId } }, { attendees: { $elemMatch: { id: currentUserId, type: "user" } } }],
    });
  }

  filters.customBlocks?.forEach((block) => {
    const compiled = compileBlock(block);
    if (compiled && Object.keys(compiled).length) {
      andClauses.push(compiled);
    }
  });

  if (andClauses.length) {
    top.$and = andClauses;
  }

  return top as CalendarEventMdbFilter;
};

export const countActiveCalendarFilters = (filters: CalendarFilters | undefined): number => {
  if (!filters) return 0;
  let count = 0;
  if (filters.templateIds?.length) count++;
  if (filters.locationIds?.length) count++;
  if (filters.userIds?.length) count++;
  if (filters.enduserIds?.length) count++;
  if (filters.hideCancelled) count++;
  if (filters.hideNoShow) count++;
  if (filters.hideRescheduled) count++;
  if (filters.hideEventsICreatedButAmNotAttending) count++;
  filters.customBlocks?.forEach((block) => {
    if (block.filters.some(isFreeformFilterValueComplete)) count++;
  });
  return count;
};
